import { defineStore } from 'pinia'
import { computed, watch, ref } from 'vue'
import type { JwtContent } from '@/types'
import { JwtParser } from '@/composables'
import { getActiveRouter } from '@/router'
import axios from 'axios'
import { isClient, safeLocalStorage } from '@/composables/useSSR'
import { usePermissionsStore } from './permissions'

// Configuration constants
const REFRESH_THRESHOLD_SECONDS = 300 // 5 minutes before expiry
const CHECK_INTERVAL_MS = 30_000 // Check every 30 seconds

export const useJwtStore = defineStore('jwt', () => {
  // Read JWT from localStorage synchronously at initialization (SSR-safe)
  // This ensures JWT is available immediately, not waiting for onMounted()
  const storage = isClient ? safeLocalStorage() : null
  let initialJwt = storage?.getItem('jwt') ?? null

  // Validate stored JWT at load time — clear corrupt or expired tokens immediately
  if (initialJwt) {
    const parsed = JwtParser(initialJwt)
    if (!parsed || typeof parsed.exp !== 'number' || Math.floor(Date.now() / 1000) > parsed.exp) {
      storage?.removeItem('jwt')
      initialJwt = null
    }
  }

  const state = ref<string | null>(initialJwt)
  // Temporary storage for JWT during OTP verification (NOT persisted to localStorage)
  const pendingOtpToken = ref<string | null>(null)
  // Track if watcher is active to prevent multiple registrations
  const watcherActive = ref(false)
  /** Shared in-flight refresh so concurrent callers dedup to one request. */
  let refreshInFlight: Promise<boolean> | null = null

  // GETTER
  const getJwt = computed((): string | null => {
    return state.value
  })

  const getJwtContent = computed((): JwtContent | null => {
    if (!state.value) return null
    return JwtParser(state.value)
  })

  // Get pending token for OTP verification (from memory, not localStorage)
  const getPendingToken = computed((): string | null => {
    return pendingOtpToken.value
  })

  // SETTER
  function setJwt(token: string) {
    const previousIdentity = getJwtContent.value?.id

    state.value = token
    const storage = safeLocalStorage()
    if (storage) {
      storage.setItem('jwt', token)
    }
    pendingOtpToken.value = null // Clear pending token once fully authenticated

    // Re-initialize permissions from the new JWT. When the underlying account
    // identity changes (e.g. impersonation start/end, account switch), also
    // invalidate the cached manageableDomains/delegations so the new identity
    // fetches a fresh list. Refresh-token rotations keep the same id and skip
    // the refetch to avoid hammering /account/manageable-domains every 5 min.
    try {
      const parsed = JwtParser(token)
      if (parsed) {
        const permissionsStore = usePermissionsStore()
        const identityChanged = previousIdentity !== parsed.id
        if (identityChanged) {
          permissionsStore.clearPermissions()
        }
        permissionsStore.initFromJWT(parsed)
        if (identityChanged) {
          void Promise.all([
            permissionsStore.fetchDelegations(),
            permissionsStore.fetchManageableDomains()
          ])
        }
      }
    } catch {
      // Ignore parsing errors - permissions will be empty
    }
  }

  // Store JWT temporarily during OTP step (NOT saved to localStorage)
  function setPendingToken(token: string) {
    pendingOtpToken.value = token
  }

  // Clear pending token (on OTP cancel/failure/timeout)
  function clearPendingToken() {
    pendingOtpToken.value = null
  }

  function removeJwt() {
    state.value = null
    pendingOtpToken.value = null
    const storage = safeLocalStorage()
    if (storage) {
      storage.removeItem('jwt')
    }
    // Clear permissions on logout
    const permissionsStore = usePermissionsStore()
    permissionsStore.clearPermissions()
  }

  // Handle JWT removal - redirect to landing page
  function handleJwtRemoved() {
    const router = getActiveRouter()
    if (!router) return

    // Only redirect if on a dashboard route
    const currentRoute = router.currentRoute.value
    if (currentRoute.path.startsWith('/dashboard')) {
      router.push({ name: 'login' })
    }
  }

  // Check if JWT is valid (exists and not expired)
  function isJwtValid(token: string | null): boolean {
    if (!token) return false
    try {
      const parsed = JwtParser(token)
      if (parsed && typeof parsed.exp === 'number') {
        const unixNow = Math.floor(Date.now() / 1000)
        return unixNow <= parsed.exp
      }
    } catch {
      // Invalid token
    }
    return false
  }

  // Check if token should be refreshed (within threshold of expiry)
  function shouldRefresh(token: string | null): boolean {
    if (!token) return false
    try {
      const parsed = JwtParser(token)
      if (parsed?.exp) {
        const now = Math.floor(Date.now() / 1000)
        const timeLeft = parsed.exp - now
        // Refresh if less than threshold remaining but not already expired
        return timeLeft > 0 && timeLeft < REFRESH_THRESHOLD_SECONDS
      }
    } catch {
      // Invalid token
    }
    return false
  }

  /**
   * Refresh the token by calling the backend.
   *
   * Concurrent callers share one request and all receive its result. Returning
   * `false` to a caller that merely arrived mid-refresh would be read as
   * "refresh failed" by the axios interceptor, which logs the user out on a
   * session that is in fact about to be renewed.
   */
  async function refreshToken(): Promise<boolean> {
    if (!state.value) return false
    if (refreshInFlight) return refreshInFlight

    refreshInFlight = doRefresh(state.value).finally(() => {
      refreshInFlight = null
    })
    return refreshInFlight
  }

  async function doRefresh(token: string): Promise<boolean> {
    try {
      const response = await axios.post(
        '/account/refresh',
        null,
        {
          baseURL: import.meta.env.VITE_BASE_URL,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          },
          timeout: 10_000
        }
      )

      // Install only if nothing changed the token while this refresh was in
      // flight. A logout must win, and so must an impersonation start or end,
      // which swap the token for a different one: overwriting either would
      // reinstate a session the user just left.
      if (response.data?.token && state.value === token) {
        setJwt(response.data.token)
        return true
      }
    } catch (error: unknown) {
      // Refresh failed - check if it's an auth error
      const axiosError = error as { response?: { status?: number } }
      if (axiosError?.response?.status === 401) {
        // Token is invalid, must re-login
        removeJwt()
        handleJwtRemoved()
      }
      // For other errors (network, server), don't logout - let it retry next interval
    }
    return false
  }

  // Storage event handler for cross-tab sync
  let storageHandler: ((event: StorageEvent) => void) | null = null
  // Interval for periodic expiration check
  let expirationInterval: ReturnType<typeof setInterval> | null = null
  // Visibility change handler for tab-return refresh
  let visibilityHandler: (() => void) | null = null

  // Start watching JWT for removal/expiration
  function startJwtWatcher() {
    if (watcherActive.value || !isClient) return // Already watching or SSR

    watcherActive.value = true

    // Watch reactive state for same-tab changes
    watch(
      () => state.value,
      (newValue, oldValue) => {
        // Only trigger if we had a token and now it's gone or invalid
        if (oldValue && !isJwtValid(newValue)) {
          handleJwtRemoved()
        }
      }
    )

    // Listen to storage events for cross-tab changes
    storageHandler = (event: StorageEvent) => {
      if (event.key === 'jwt') {
        // Sync state with localStorage
        state.value = event.newValue

        // If JWT was removed or changed to invalid, redirect
        if (!isJwtValid(event.newValue)) {
          handleJwtRemoved()
        }
      }
    }
    window.addEventListener('storage', storageHandler)

    // Visibility change handler - refresh token when user returns to tab
    visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        const storage = safeLocalStorage()
        const token = storage?.getItem('jwt') ?? null
        if (token && shouldRefresh(token)) {
          refreshToken()
        } else if (token && !isJwtValid(token)) {
          // Token expired while tab was in background
          removeJwt()
          handleJwtRemoved()
        }
      }
    }
    document.addEventListener('visibilitychange', visibilityHandler)

    // Periodic check every 30 seconds
    // Proactively refresh token before it expires
    expirationInterval = setInterval(async () => {
      const storage = safeLocalStorage()
      const currentToken = storage?.getItem('jwt') ?? null

      // Sync state with localStorage (detect external clearing)
      if (state.value !== currentToken) {
        state.value = currentToken
      }

      // No token - redirect if on dashboard
      if (!currentToken) {
        handleJwtRemoved()
        return
      }

      // Try to refresh if token is expiring soon
      if (shouldRefresh(currentToken)) {
        await refreshToken()
        return
      }

      // Only logout if token is actually expired (not just expiring)
      if (!isJwtValid(currentToken)) {
        removeJwt()
        handleJwtRemoved()
      }
    }, CHECK_INTERVAL_MS)
  }

  // Stop watching (cleanup)
  function stopJwtWatcher() {
    if (!isClient) return

    if (storageHandler) {
      window.removeEventListener('storage', storageHandler)
      storageHandler = null
    }
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
    if (expirationInterval) {
      clearInterval(expirationInterval)
      expirationInterval = null
    }
    watcherActive.value = false
  }

  return {
    state,
    setJwt,
    getJwt,
    getJwtContent,
    removeJwt,
    setPendingToken,
    getPendingToken,
    clearPendingToken,
    startJwtWatcher,
    stopJwtWatcher,
    isJwtValid,
    shouldRefresh,
    refreshToken
  }
})
